import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './invoice-details-screen-content.scss';
import { useInvoiceDetails } from '../hooks';
import {
  DocViewCard,
  ListSkeleton,
  PullRefreshContainer,
} from '../../components';
import { InvoiceCard } from '../invoice-card';
import { InvoiceDetails } from './invoice-details';
import { Invoice, InvoiceDocs } from '@zonofi/zono-money-store';
import { TouchableOpacity } from '@zonofi/seller-hub/common';

export interface InvoiceDetailsScreenContentProps {
  updateInvoiceDetails?: (invoice: Invoice) => void;
}

type TabType = 'lineItems' | 'documents';
const TABS: { key: TabType; label: string }[] = [
  { key: 'lineItems', label: 'Line Items' },
  { key: 'documents', label: 'Documents' },
];

export const InvoiceDetailsScreenContent: React.FC<
  InvoiceDetailsScreenContentProps
> = ({ updateInvoiceDetails = () => {} }) => {
  const { invoiceDetailsRes, refresh } = useInvoiceDetails();
  const data = invoiceDetailsRes.data;

  const hasLines = useMemo(
    () => (data?.lines?.length ?? 0) > 0,
    [data?.lines?.length]
  );
  const hasDocs = useMemo(
    () => (data?.docs?.length ?? 0) > 0,
    [data?.docs?.length]
  );

  const tabVisible = useCallback(
    (key: TabType) => (key === 'lineItems' ? hasLines : hasDocs),
    [hasLines, hasDocs]
  );

  const [activeTab, setActiveTab] = useState<TabType>('lineItems');

  useEffect(() => {
    if (!data) return;
    updateInvoiceDetails(data);
    setActiveTab(hasLines ? 'lineItems' : 'documents');
  }, [data]);

  const selectedDoc = useMemo(
    () =>
      data?.docs?.find((doc) => doc.subtype === 'official') ?? data?.docs?.[0],
    [data]
  );

  return (
    <PullRefreshContainer onRefresh={refresh}>
      {invoiceDetailsRes.isLoading ? (
        <ListSkeleton />
      ) : (
        <div className="invoice-details-screen">
          {invoiceDetailsRes.isSuccess && data && (
            <InvoiceCard invoiceItem={data} showShare={false} />
          )}

          {(hasLines || hasDocs) && (
            <div className="invoice-tab-toggle">
              {TABS.filter(({ key }) => tabVisible(key)).map(
                ({ key, label }) => (
                  <TouchableOpacity
                    key={key}
                    className={`invoice-tab-btn${activeTab === key ? ' active' : ''} font-regular`}
                    onPress={() => setActiveTab(key)}
                  >
                    {label}
                  </TouchableOpacity>
                )
              )}
            </div>
          )}

          {hasLines && activeTab === 'lineItems' && (
            <InvoiceDetails invoiceDetails={data as Invoice} />
          )}

          {hasDocs && activeTab === 'documents' && (
            <DocViewCard docs={selectedDoc as InvoiceDocs} />
          )}
        </div>
      )}
    </PullRefreshContainer>
  );
};
